import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useRef } from "react";
import { ArrowDownRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1709497197725-2e97c76b31d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtZWRpY2FsJTIwYnVpbGRpbmclMjBleHRlcmlvciUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODUzNTMyNTF8MA&ixlib=rb-4.1.0&q=85";

const lines = ["Aligning healthcare", "organizations for", "long-term success."];

export default function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 140]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.12]);

  const go = () => document.querySelector("#packages")?.scrollIntoView({ behavior: "smooth" });
  const goContact = () => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden pt-28 pb-16">
      <motion.div style={{ y, scale }} className="absolute inset-0 -z-10">
        <img src={HERO_IMG} alt="Modern medical architecture" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-background/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
      </motion.div>

      <div className="mx-auto max-w-[1440px] w-full px-6 md:px-12">
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground"
          data-testid="hero-overline"
        >
          Align Enrollment Solutions LLC
        </motion.span>

        <h1 className="mt-6 font-serif font-semibold text-foreground text-[12vw] leading-[1.02] tracking-tighter sm:text-6xl md:text-7xl lg:text-8xl">
          {lines.map((line, i) => (
            <span key={i} className="line-mask pb-[0.12em]">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ delay: 0.35 + i * 0.12, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <p className="max-w-md text-base text-muted-foreground leading-relaxed">
            We manage the full lifecycle of provider and group payer enrollment — credentialing, contracting, and
            revalidation — so your clinicians can start seeing patients, in-network, faster.
          </p>
          <div className="flex items-center gap-4">
            <button data-testid="hero-primary-cta" onClick={goContact} className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm text-primary-foreground transition-transform duration-200 hover:-translate-y-0.5">
              Start Enrollment <ArrowDownRight size={16} />
            </button>
            <button data-testid="hero-secondary-cta" onClick={go} className="text-sm font-mono uppercase tracking-[0.15em] text-foreground underline underline-offset-8 decoration-foreground/30 hover:decoration-foreground transition-colors duration-200">
              View Packages
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
